import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BedDouble,
  Bath,
  Car,
  Maximize,
  MapPin,
  Check,
  MessageCircle,
} from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { formatBRL } from "@/lib/scrollTo";
import { resolveImg } from "@/lib/img";
import { CONTATO } from "@/content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImovelDetalhe = () => {
  const { slug } = useParams();
  const [imovel, setImovel] = useState(null);
  const [erro, setErro] = useState(false);
  const [fotoAtiva, setFotoAtiva] = useState(0);

  useEffect(() => {
    setImovel(null);
    setErro(false);
    setFotoAtiva(0);
    axios
      .get(`${API}/properties/${slug}`)
      .then((res) => setImovel(res.data))
      .catch(() => setErro(true));
  }, [slug]);

  if (erro) {
    return (
      <main className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-6 pt-24" data-testid="property-not-found">
        <p className="font-serif text-2xl text-stone-300">Imóvel não encontrado.</p>
        <Link to="/imoveis" data-testid="back-to-catalog-link" className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-brand">
          Voltar aos imóveis
        </Link>
      </main>
    );
  }

  if (!imovel) {
    return (
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10" data-testid="property-loading">
        <div className="h-[60vh] animate-pulse bg-coal" />
      </main>
    );
  }

  const specs = [
    { icon: Maximize, label: "Área", valor: `${imovel.area} m²` },
    { icon: BedDouble, label: "Quartos", valor: imovel.quartos },
    { icon: BedDouble, label: "Suítes", valor: imovel.suites },
    { icon: Bath, label: "Banheiros", valor: imovel.banheiros },
    { icon: Car, label: "Vagas", valor: imovel.vagas },
  ].filter((s) => s.label === "Área" || s.valor > 0);

  return (
    <main className="mx-auto max-w-7xl px-6 pb-24 pt-28 lg:px-10 lg:pt-36">
      <Reveal>
        <Link
          to="/imoveis"
          data-testid="back-to-catalog-link"
          className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400 transition-colors duration-300 hover:text-brand"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Todos os imóveis
        </Link>
      </Reveal>

      <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Reveal>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-brand">
              <MapPin size={12} /> {imovel.bairro} — {imovel.cidade}
            </p>
          </Reveal>
          <MaskedLines
            className="mt-3 font-serif text-3xl leading-tight tracking-tight text-[#F4F4F6] sm:text-4xl lg:text-5xl"
            lines={[imovel.titulo]}
          />
        </div>
        <Reveal delay={0.2}>
          <div className="flex flex-col items-start gap-2 lg:items-end">
            <span className="bg-brand px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white" data-testid="property-negocio">
              {imovel.negocio}
            </span>
            <p className="font-serif text-3xl text-[#F4F4F6] sm:text-4xl" data-testid="property-price">
              {formatBRL(imovel.preco, imovel.negocio)}
            </p>
          </div>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-4 lg:grid-cols-12">
        <Reveal className="lg:col-span-8">
          <div className="relative aspect-[16/10] overflow-hidden border border-white/10" data-testid="gallery-main">
            <AnimatePresence mode="wait">
              <motion.img
                key={fotoAtiva}
                src={resolveImg(imovel.imagens[fotoAtiva])}
                alt={`${imovel.titulo} — foto ${fotoAtiva + 1}`}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <span className="absolute left-5 top-5 border border-white/20 bg-obsidian/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-200 backdrop-blur-md">
              {imovel.tipo}
            </span>
          </div>
        </Reveal>
        <div className="grid grid-cols-4 gap-4 lg:col-span-4 lg:grid-cols-2">
          {imovel.imagens.slice(0, 4).map((src, i) => (
            <button
              key={i}
              onClick={() => setFotoAtiva(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`relative overflow-hidden border transition-colors duration-300 ${
                fotoAtiva === i ? "border-brand" : "border-white/10 hover:border-white/30"
              }`}
            >
              <img src={resolveImg(src)} alt={`Miniatura ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              {fotoAtiva !== i && <div className="absolute inset-0 bg-obsidian/40" />}
            </button>
          ))}
        </div>
      </div>

      <Reveal>
        <div data-testid="specs-strip" className={`mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/10 ${specs.length === 5 ? "sm:grid-cols-5" : specs.length === 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
          {specs.map((s) => (
            <div key={s.label} className="flex flex-col items-center bg-obsidian px-4 py-7">
              <s.icon size={18} className="text-brand" />
              <p className="mt-3 font-serif text-xl text-[#F4F4F6]">{s.valor}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{s.label}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-16 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-brand">Sobre o imóvel</p>
            <p className="mt-6 text-base font-light leading-relaxed text-stone-300 sm:text-lg" data-testid="property-description">
              {imovel.descricao}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.35em] text-brand">Características</p>
            <ul className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="features-list">
              {imovel.caracteristicas.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm font-light text-stone-300">
                  <Check size={15} className="mt-0.5 shrink-0 text-brand" />
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <div className="sticky top-24 border border-white/10 bg-coal p-8" data-testid="visit-scheduler">
              <p className="font-serif text-2xl text-[#F4F4F6]">Agende uma visita</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-stone-400">
                Preencha seus dados e entraremos em contato para combinar o melhor horário.
              </p>
              <div className="mt-8">
                <LeadForm tipo="visita" imovelSlug={imovel.slug} testidPrefix="visit" />
              </div>
              <a
                href={`https://wa.me/${CONTATO.whatsapp}?text=${encodeURIComponent(`Olá! Tenho interesse no imóvel "${imovel.titulo}" (${imovel.bairro}, ${imovel.cidade}) que vi no site da NB Imóveis.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="mt-5 flex items-center justify-center gap-3 border border-white/15 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-300 transition-colors duration-300 hover:border-brand/60 hover:text-brand"
              >
                <MessageCircle size={15} /> Falar no WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
};

export default ImovelDetalhe;
